import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GitBranch, MapPin, Loader2, Save, Clock, X, Info, Upload } from 'lucide-react';
import { BulkImportModal } from '../ui/BulkImportModal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';

export function BranchesList({ branches, onUpdateBranches, selectedBranchId, focusedId, onSelect, readOnly, onRefresh }) {
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importEntity, setImportEntity] = useState('branches');

  const openNew = () => {
    onSelect({ id: 'new', name: '', code: '', rooms: [] }, 'branch');
  };

  // Compute global card index offset: branches are first in the flat list
  const getGlobalIndex = (localIdx) => localIdx;

  return (
    <>
      <BulkImportModal
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        entityName={importEntity}
        onImportComplete={() => { onRefresh?.(); }}
      />
      <div className="flex flex-col h-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700/50 rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700/50">
          <div>
            <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100 tracking-tight">Branches & Locations</h2>
            <p className="text-[0.65rem] text-zinc-500 mt-0.5">{branches.length} configured</p>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 px-2 flex items-center gap-1"
                onClick={() => { setImportEntity('branches'); setBulkImportOpen(true); }}
                title="Bulk Import Branches"
              >
                <Upload className="w-3.5 h-3.5" />
                <span className="text-[10px] hidden md:inline">Branches</span>
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 px-2 flex items-center gap-1"
                onClick={() => { setImportEntity('travel_times'); setBulkImportOpen(true); }}
                title="Bulk Import Travel Times"
              >
                <Upload className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-[10px] hidden md:inline">Travel Times</span>
              </Button>
              <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          )}
        </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {branches.map((branch, localIdx) => (
            <div key={branch.id}
              className={cn(
                "data-card !p-3 cursor-pointer transition-all",
                selectedBranchId === branch.id && "ring-2 ring-blue-500/30",
                focusedId === branch.id && "ring-2 ring-violet-500/50 shadow-md"
              )}
              onClick={() => onSelect(branch, 'branch')}
              data-card-index={getGlobalIndex(localIdx)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-blue-50 dark:bg-blue-900/20">
                    <GitBranch className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{branch.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500">
                      <Badge variant="secondary" className="text-[0.55rem] h-4">{branch.code}</Badge>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {(branch.rooms || []).length} rooms</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {branches.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No branches yet.</p>}
        </div>
      </ScrollArea>
    </div>
    </>
  );
}

export function BranchDetail({ branch, branches, travelTimes, onUpdateBranches, onUpdateTravelTimes, onClose, readOnly }) {
  const [editMode, setEditMode] = useState(branch?.id === 'new');
  const [editForm, setEditForm] = useState({});
  const [matrixValues, setMatrixValues] = useState({});
  const [matrixDirty, setMatrixDirty] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);

  // Sync edit state and values when branch changes
  React.useEffect(() => {
    if (branch?.id === 'new') {
      setEditMode(true);
      setEditForm({ name: '', code: '', rooms: '' });
    } else {
      setEditMode(false);
      setEditForm({});
    }
  }, [branch?.id]);

  // Initialize Matrix values only when travelTimes changes
  React.useEffect(() => {
    const mv = {};
    travelTimes.forEach(t => { mv[`${t.branch_from_id}_${t.branch_to_id}`] = t.minutes; });
    setMatrixValues(mv);
    setMatrixDirty(false);
  }, [travelTimes]);

  const startEdit = () => {
    setEditMode(true);
    setEditForm({ name: branch.name, code: branch.code, rooms: (branch.rooms || []).join(', ') });
  };

  const saveEdit = async () => {
    if (!editForm.name || !editForm.code) { toast.error('Name and code are required'); return; }
    const rooms = (editForm.rooms || '').split(',').map(r => r.trim()).filter(Boolean);
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      if (branch.id === 'new') {
        const { data, error } = await supabase
          .from('branches')
          .insert({ name: editForm.name, code: editForm.code, rooms, team_id })
          .select()
          .single();
        if (error) throw error;
        onUpdateBranches(prev => [...prev, data]);
        toast.success('Branch created');
        onClose();
      } else {
        const { data, error } = await supabase
          .from('branches')
          .update({ name: editForm.name, code: editForm.code, rooms })
          .eq('id', branch.id)
          .eq('team_id', team_id)
          .select()
          .single();
        if (error) throw error;
        onUpdateBranches(prev => prev.map(b => b.id === branch.id ? data : b));
        toast.success('Branch updated');
        setEditMode(false);
      }
    } catch (err) { toast.error(err.message || 'Failed to save branch'); }
  };

  const remove = async () => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const team_id = profile.team_id;
      const { error } = await supabase.from('branches').delete().eq('id', branch.id).eq('team_id', team_id);
      if (error) throw error;
      onUpdateBranches(prev => prev.filter(b => b.id !== branch.id));
      onClose();
      toast.success('Branch deleted');
    } catch (err) { toast.error(err.message || 'Failed to delete branch'); }
  };

  const setMatrixValue = (fromId, toId, val) => {
    const mins = parseInt(val) || 0;
    setMatrixValues(prev => ({
      ...prev,
      [`${fromId}_${toId}`]: mins,
      [`${toId}_${fromId}`]: mins,
    }));
    setMatrixDirty(true);
  };

  const saveMatrix = async () => {
    setSavingMatrix(true);
    try {
      const pairs = [];
      const seen = new Set();
      for (const key of Object.keys(matrixValues)) {
        const [fromId, toId] = key.split('_');
        if (fromId === toId) continue;
        const pairKey = [fromId, toId].sort().join('_');
        if (seen.has(pairKey)) continue;
        seen.add(pairKey);
        const minutes = matrixValues[key] || 0;
        pairs.push({ branch_from_id: fromId, branch_to_id: toId, minutes });
        pairs.push({ branch_from_id: toId, branch_to_id: fromId, minutes });
      }
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      for (const pair of pairs) {
        const { error } = await supabase
          .from('travel_times')
          .upsert({ ...pair, team_id: profile.team_id }, { onConflict: 'branch_from_id,branch_to_id' });
        if (error) throw error;
      }
      const { data, error } = await supabase.from('travel_times').select('*').eq('team_id', profile.team_id);
      if (error) throw error;
      onUpdateTravelTimes(() => data);
      toast.success('Travel matrix saved');
    } catch (err) { toast.error(err.message || 'Failed to save travel times'); }
    setSavingMatrix(false);
  };

  const getMatrixValue = (fromId, toId) => matrixValues[`${fromId}_${toId}`] ?? '';

  if (!branch) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-900 animate-slide-in-right">
      <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/50 flex flex-shrink-0 items-center justify-between">
        <h3 className="text-base font-semibold truncate">{branch.id === 'new' ? 'Create Branch' : (editMode ? 'Edit Branch' : branch.name)}</h3>
        <div className="flex items-center gap-1">
          {editMode ? (
            <>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdit}>
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              {branch.id !== 'new' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditMode(false)}>Cancel</Button>
              )}
            </>
          ) : (
            !readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            )
          )}
          <button className="w-7 h-7 rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center justify-center text-zinc-400" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-5">
        {editMode ? (
          <div className="space-y-3 mb-6">
            <div><Label className="text-xs">Name</Label><Input className="mt-1" value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Computer Science" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Code</Label><Input className="mt-1" value={editForm.code || ''} onChange={e => setEditForm(f => ({ ...f, code: e.target.value }))} placeholder="e.g. CS" /></div>
            </div>
            <div><Label className="text-xs">Rooms (comma separated)</Label><Input className="mt-1" value={editForm.rooms || ''} onChange={e => setEditForm(f => ({ ...f, rooms: e.target.value }))} placeholder="e.g. Room 101, Lab B" /></div>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">{branch.code}</Badge>
              <span className="text-xs text-zinc-500">{(branch.rooms || []).length} Rooms configured</span>
            </div>
            <div className="p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/50">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-1 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Rooms</p>
              <p className="text-sm text-zinc-500 leading-relaxed">{(branch.rooms || []).join(', ') || 'No rooms added.'}</p>
            </div>
          </div>
        )}

        {/* Global Travel Matrix */}
        {branch.id !== 'new' && (
          <div className="mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-500" />
                <div>
                  <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">Travel Times Matrix</h3>
                  <p className="text-[0.6rem] text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                    Enter the estimated travel duration in <b>minutes</b> between these branch locations. 
                    <br />
                    <span className="text-blue-500 dark:text-blue-400 font-medium">Why this matters:</span> The scheduler uses this data to prevent faculty from being booked in back-to-back sessions at different locations without sufficient transit time.
                  </p>
                </div>
              </div>
              {matrixDirty && (
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveMatrix} disabled={savingMatrix}>
                  {savingMatrix ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save Matrix
                </Button>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <td className="bg-zinc-50 dark:bg-zinc-800 p-2 font-medium text-zinc-500 border-b border-zinc-200 dark:border-zinc-700" />
                    {branches.map(b => (
                      <td key={b.id} className="bg-zinc-50 dark:bg-zinc-800 p-2 font-medium text-zinc-500 border-b border-l border-zinc-200 dark:border-zinc-700 text-center truncate max-w-[60px]" title={b.name}>
                        {b.code}
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {branches.map(row => (
                    <tr key={row.id}>
                      <td className="p-2 font-medium text-zinc-500 border-b border-zinc-200 dark:border-zinc-700 truncate max-w-[80px]" title={row.name}>
                        {row.code}
                      </td>
                      {branches.map(col => {
                        if (row.id === col.id) return <td key={col.id} className="p-2 border-b border-l border-zinc-200 dark:border-zinc-700 text-center bg-zinc-50 dark:bg-zinc-800 text-zinc-300">-</td>;
                        return (
                          <td key={col.id} className="p-0 border-b border-l border-zinc-200 dark:border-zinc-700 group relative">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={getMatrixValue(row.id, col.id)}
                              onChange={e => {
                                const val = e.target.value.replace(/\D/g, '');
                                setMatrixValue(row.id, col.id, val);
                              }}
                              className="w-full h-full text-center bg-transparent border-0 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/10 p-2 text-zinc-800 dark:text-zinc-200 pr-4"
                            />
                            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[0.55rem] text-zinc-400 group-focus-within:text-blue-500 pointer-events-none">
                              min
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!readOnly && branch.id !== 'new' && (
          <Button variant="ghost" size="sm" className="mt-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Branch
          </Button>
        )}
      </ScrollArea>
    </div>
  );
}
